<bit-table-v2
  #vaultTable="bitTableV2"
  [tableDef]="table"
  [displayedColumns]="visibleColumns()"
  [filter]="filter"
  [filters]="initialFilterValues()"
  [loading]="loading()"
  [selection]="selection"
  [trackBy]="trackByCipherId"
  [virtualRowHeight]="rowHeight"
  [queryParam]="filterNamespace"
  height="fill"
  (selectedChange)="selectedChange.emit($event)"
>
  <bit-table-toolbar>
    <bit-search class="tw-flex-1" [placeholder]="'search' | i18n"></bit-search>

    <!-- Page-level controls (Import, Add, ...) are the host's concern, not the table's. -->
    <ng-content select="[slot=toolbar]" slot="end"></ng-content>

    <bit-filter-menu
      [key]="filterKeys.type"
      [placeholderText]="'type' | i18n"
      [unsetLabel]="'all' | i18n"
    >
      @for (cipherType of availableCipherTypes(); track cipherType) {
        <bit-filter-option [value]="cipherType">
          {{ cipherTypeLabel(cipherType) | i18n }}
        </bit-filter-option>
      }
    </bit-filter-menu>

    <bit-filter-divider></bit-filter-divider>

    <bit-filter-toggle
      [key]="filterKeys.favorites"
      [label]="'favorites' | i18n"
      icon="bwi-star"
      iconActive="bwi-star-f"
      [disabled]="noFavorites()"
      [disabledTooltip]="favoritesDisabledTooltip()"
    ></bit-filter-toggle>

    @if (showVaults()) {
      <bit-filter-menu [key]="filterKeys.vault" [placeholderText]="'vault' | i18n" multiple>
        @if (showMyVaultOption()) {
          <bit-filter-option [value]="MY_VAULT" [iconTile]="myVaultFilterTile()">{{
            "myVault" | i18n
          }}</bit-filter-option>
        }
        @for (organization of sortedOrganizations(); track organization.id) {
          <bit-filter-option
            [value]="organization.id"
            [iconTile]="organizationTiles().get(organization.id)"
            >{{ organization.name }}</bit-filter-option
          >
        }
      </bit-filter-menu>
    }

    @if (showMyItems()) {
      <bit-filter-toggle
        [key]="filterKeys.myItems"
        [label]="'myItemsV2' | i18n"
        icon="bwi-user"
        [disabled]="noMyItems()"
        [disabledTooltip]="myItemsDisabledTooltip()"
      ></bit-filter-toggle>
    }

    @if (showSharedFolders()) {
      <bit-filter-menu
        [key]="filterKeys.sharedFolder"
        [placeholderText]="'sharedFolders' | i18n"
        multiple
        [disabled]="noSharedFolderOptions()"
        [disabledTooltip]="sharedFolderDisabledTooltip()"
        [options]="groupSharedFolders() ? [] : nestedSharedFolders()"
      >
        @if (groupSharedFolders()) {
          @for (group of groupedSharedFolders(); track group.organizationId) {
            <bit-filter-section
              [label]="group.name"
              collapsible
              [options]="group.collections"
            ></bit-filter-section>
          }
        }
      </bit-filter-menu>
    }

    <bit-filter-menu
      [key]="filterKeys.folder"
      [placeholderText]="'myFolders' | i18n"
      multiple
      [disabled]="noFolders()"
      [disabledTooltip]="foldersDisabledTooltip()"
      [options]="nestedFolders()"
    ></bit-filter-menu>
  </bit-table-toolbar>

  <vault-empty-vault
    slot="empty"
    [hasItems]="ciphers().length > 0"
    [filterValues]="vaultTable.filterValues()"
    [scope]="scope()"
    [organizationName]="organizationName()"
    [hasMultipleVaults]="hasMultipleVaults()"
    [sharedFolderName]="sharedFolderName()"
    [defaultCollectionId]="defaultCollectionId()"
    (clearSearch)="clearSearch(vaultTable)"
    (clearFilters)="clearChipFilters(vaultTable)"
  >
    <ng-content slot="empty-add-item" select="[slot=empty-add-item]"></ng-content>
  </vault-empty-vault>

  <!-- Name -->
  <bit-column sortable defaultSort="asc" width="minmax(240px, 480px)">
    <bit-header-cell>{{ "name" | i18n }}</bit-header-cell>

    <bit-cell
      *bitCellDef="table.columns.name; let row"
      [class.tw-cursor-pointer]="itemAction() != null"
      (click)="onCellActivate(row, $event)"
    >
      <app-vault-icon slot="start" [cipher]="row" />

      <span class="tw-flex tw-min-w-0 tw-items-center tw-gap-1.5">
        @if (itemAction()) {
          <button
            type="button"
            bitLink
            class="tw-truncate tw-text-start"
            [title]="'editItemWithName' | i18n: row.name"
            (click)="itemAction()!(row)"
          >
            {{ row.name }}
          </button>
        } @else {
          <span class="tw-truncate">{{ row.name }}</span>
        }
        @if (row.organizationId) {
          <bit-icon
            name="bwi-business"
            class="tw-shrink-0 tw-text-muted"
            [ariaLabel]="'organization' | i18n"
          />
        }
        @if (CipherViewLikeUtils.hasAttachments(row)) {
          <bit-icon
            name="bwi-paperclip"
            class="tw-shrink-0 tw-text-muted"
            [ariaLabel]="'attachments' | i18n"
          />
        }
        @if (row.favorite) {
          <bit-icon
            name="bwi-star-f"
            class="tw-shrink-0 tw-text-primary-600"
            [ariaLabel]="'favorite' | i18n"
          />
        }
      </span>

      <span slot="secondary">{{ subtitle(row) }}</span>
    </bit-cell>

    <!-- Two lines of content, so the default single-bar skeleton would under-represent it. -->
    <bit-cell *bitCellLoading>
      <bit-skeleton-text class="tw-w-1/2 tw-mb-1" />
      <bit-skeleton-text slot="secondary" class="tw-w-1/3" />
    </bit-cell>
  </bit-column>

  <!-- Vault -->
  <bit-column sortable [sortFn]="sortByVault" width="minmax(176px, 280px)">
    <bit-header-cell>{{ "vault" | i18n }}</bit-header-cell>
    <bit-cell
      *bitCellDef="table.columns.vault; let row"
      [class.tw-cursor-pointer]="itemAction() != null"
      (click)="onCellActivate(row, $event)"
    >
      @let vaultTile = vaultIconTile(row);
      <bit-icon-tile
        slot="start"
        [icon]="vaultTile.icon"
        [variant]="vaultTile.variant ?? 'primary'"
        [color]="vaultTile.color"
        [emphasis]="vaultTile.emphasis ?? 'subtle'"
        size="xs"
      />
      {{ vaultName(row) }}
    </bit-cell>
  </bit-column>

  <!-- Shared folders -->
  <bit-column sortable [sortFn]="sortBySharedFolders" width="minmax(176px, 280px)">
    <bit-header-cell>{{ "sharedFolders" | i18n }}</bit-header-cell>
    <bit-cell
      *bitCellDef="table.columns.sharedFolders; let row"
      [class.tw-cursor-pointer]="itemAction() != null"
      (click)="onCellActivate(row, $event)"
    >
      @let sharedFolders = sharedFolderChips(row);
      @if (sharedFolders.length) {
        <bit-chip-group
          class="tw-block tw-w-full tw-min-w-0"
          [chips]="sharedFolders"
          [accessibleName]="'sharedFolders' | i18n"
          (chipSelect)="filterTo(vaultTable, filterKeys.sharedFolder, $event)"
        />
      } @else {
        <span class="tw-text-muted" aria-hidden="true">&mdash;</span>
        <span class="tw-sr-only">{{ "noSharedFolder" | i18n }}</span>
      }
    </bit-cell>
  </bit-column>

  <!-- My folders -->
  <bit-column sortable [sortFn]="sortByFolders" width="minmax(140px, 1fr)">
    <bit-header-cell>{{ "myFolders" | i18n }}</bit-header-cell>
    <bit-cell
      *bitCellDef="table.columns.myFolders; let row"
      [class.tw-cursor-pointer]="itemAction() != null"
      (click)="onCellActivate(row, $event)"
    >
      @let folders = folderChips(row);
      @if (folders.length) {
        <bit-chip-group
          class="tw-block tw-w-full tw-min-w-0"
          [chips]="folders"
          [accessibleName]="'myFolders' | i18n"
          (chipSelect)="filterTo(vaultTable, filterKeys.folder, $event)"
        />
      } @else {
        <span class="tw-text-muted" aria-hidden="true">&mdash;</span>
        <span class="tw-sr-only">{{ "noneFolder" | i18n }}</span>
      }
    </bit-cell>
  </bit-column>

  <!-- Options -->
  <vault-items-table-actions-column
    [table]="table"
    [rowActions]="rowActions()"
    [copyPresentation]="copyPresentation()"
  />
</bit-table-v2>
